import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import styles from '../SignUp.module.css'; // Reuse your color scheme/styles
import Footer from '../Footer';
import SimpleFallbackEditor from '../SimpleFallbackEditor'; // Modern Facebook Lexical editor
import TurquoiseEditor from '../TurquoiseEditor'; // New turquoise-themed editor
import './Notes.css';

// Modern SVG Icons
const BookmarkIcon = ({ filled = false, size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} role="img" aria-label="Bookmark">
        <path 
      d="M8.5 3.25h7a2 2 0 0 1 2 2v14.5l-5.5-2.75-5.5 2.75V5.25a2 2 0 0 1 2-2z" 
      fill={filled ? "currentColor" : "none"} 
      stroke="currentColor" 
      strokeWidth="1.75" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const DeleteIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} role="img" aria-label="Delete">
    <g fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 4.5h4"/>
      <path d="M4 7.5h16"/>
      <path d="M8.5 7.5v11a2 2 0 0 0 2 2h3a2 2 0 0 0 2-2v-11"/>
      <path d="M10.5 11v7"/>
      <path d="M13.5 11v7"/>
    </g>
  </svg>
);

const LockIcon = ({ size = 20, locked = true }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} role="img" aria-label={locked ? "Lock" : "Unlock"}>
    <g fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      {locked ? (
        <path d="M8 10V7a4 4 0 0 1 8 0v3"/>
      ) : (
        <path d="M8 10V7a4 4 0 0 1 8 0v1"/>
      )}
      <rect x="6" y="10" width="12" height="10" rx="2"/>
      <path d="M12 14v2"/>
      <circle cx="12" cy="13" r="0.5" fill="currentColor"/>
    </g>
  </svg>
);

const VaultIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} role="img" aria-label="Vault">
    <g fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"/>
      <circle cx="12" cy="12" r="2"/>
      <line x1="12" y1="3" x2="12" y2="7"/>
      <line x1="12" y1="17" x2="12" y2="21"/>
      <line x1="3" y1="12" x2="7" y2="12"/>
      <line x1="17" y1="12" x2="21" y2="12"/>
    </g>
  </svg>
);

const TagIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} role="img" aria-label="Tag">
    <g fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </g>
  </svg>
);

const SearchIcon = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} role="img" aria-label="Search">
    <g fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </g>
  </svg>
);

// Helper function to strip HTML tags and get formatted plain text for preview
const stripHtmlTags = (html) => {
  if (!html) return '';
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Replace certain HTML elements with appropriate spacing/formatting
  let text = html;
  // Replace paragraph breaks with double newlines
  text = text.replace(/<\/p><p>/gi, '\n\n');
  // Replace line breaks with single newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  // Replace list items with bullet points
  text = text.replace(/<li>/gi, '• ');
  text = text.replace(/<\/li>/gi, '\n');
  // Replace headers with their content
  text = text.replace(/<h[1-6][^>]*>/gi, '');
  text = text.replace(/<\/h[1-6]>/gi, '\n');
  
  // Now strip all remaining HTML tags
  tempDiv.innerHTML = text;
  let cleanText = tempDiv.textContent || tempDiv.innerText || '';
  
  // Clean up extra whitespace but preserve intentional line breaks
  cleanText = cleanText.replace(/\n\s*\n\s*\n/g, '\n\n'); // Remove triple+ newlines
  cleanText = cleanText.replace(/^\s+|\s+$/g, ''); // Trim start/end
  
  return cleanText;
};

// Helper function to create a better formatted preview
const createPreview = (content, maxLength = 100) => {
  if (!content || !content.trim()) return 'Click to add content...';
  
  const plainText = stripHtmlTags(content);
  if (!plainText) return 'Click to add content...';
  
  // If text is short enough, return as-is but replace newlines with spaces for preview
  if (plainText.length <= maxLength) {
    return plainText.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  }
  
  // For longer text, find a good breakpoint
  const previewText = plainText.substring(0, maxLength);
  const lastSpace = previewText.lastIndexOf(' ');
  const lastNewline = previewText.lastIndexOf('\n');
  
  // Use the last space or newline, whichever comes later (but not too short)
  const breakPoint = Math.max(lastSpace, lastNewline);
  const cutoff = breakPoint > maxLength * 0.8 ? breakPoint : maxLength;
  
  let preview = plainText.substring(0, cutoff).trim();
  // Replace multiple newlines with single spaces for preview
  preview = preview.replace(/\n+/g, ' ').replace(/\s+/g, ' ');
  
  return preview + '...';
};

export default function Notes() {
  // Removed excessive logging
  
  const [notes, setNotes] = useState([]);
  const [vaults, setVaults] = useState([]);
  const [currentVault, setCurrentVault] = useState(null);
  const [currentNote, setCurrentNote] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [currentTagInput, setCurrentTagInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [isMessageFadingOut, setIsMessageFadingOut] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  
  // Track original content to detect changes
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [originalTags, setOriginalTags] = useState([]);

  // Removed excessive logging
  const [showVaultModal, setShowVaultModal] = useState(false);
  const [showVaultDropdown, setShowVaultDropdown] = useState(false);
  const [newVaultName, setNewVaultName] = useState('');
  const [newVaultDescription, setNewVaultDescription] = useState('');

  const history = useHistory();

  // Refs for stable references - DISABLED for debugging
  // const titleRef = useRef();
  // const contentRef = useRef();
  // const tagsRef = useRef();

  // Update refs when values change - DISABLED
  // titleRef.current = title;
  // contentRef.current = content;
  // tagsRef.current = tags;

  // API URL
  const API_URL = 'http://localhost:5002/api';

  // Get auth token - simplified
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    // Only log auth errors, not every token check
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Handle authentication errors - simplified
  const handleAuthError = (response) => {
    if (response.status === 401 || response.status === 403) {
      // Token is invalid or expired
      localStorage.removeItem('token');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      setMessage('Session expired. Please log in again.');
      setTimeout(() => {
        history.push('/sign-in');
      }, 2000);
      return true;
    }
    return false;
  };

  // MINIMAL TAG FUNCTIONS - Testing only
  const addTag = (tagName) => {
    console.log('Adding tag:', tagName);
    const trimmedTag = tagName.trim();
    if (trimmedTag) {
      console.log('Current tags before:', tags);
      setTags(prevTags => {
        const newTags = prevTags.includes(trimmedTag) ? prevTags : [...prevTags, trimmedTag];
        console.log('New tags:', newTags);
        return newTags;
      });
    }
    setCurrentTagInput('');
  };

  const removeTag = (tagToRemove) => {
    console.log('Removing tag:', tagToRemove);
    setTags(prevTags => {
      const newTags = prevTags.filter(tag => tag !== tagToRemove);
      console.log('Tags after removal:', newTags);
      return newTags;
    });
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(currentTagInput);
    }
  };  // Calculate time remaining until note expires
  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expires = new Date(expiresAt);
    const timeLeft = expires - now;
    
    if (timeLeft <= 0) return "Expired";
    
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  // Toggle note lock status
  const toggleNoteLock = async (noteId, currentLockStatus) => {
    try {
      // Handle undefined/null currentLockStatus as false (unlocked)
      const isCurrentlyLocked = currentLockStatus === true;
      
      console.log('Toggling lock for note:', noteId, 'Current status:', isCurrentlyLocked);
      
      const response = await fetch(`${API_URL}/notes/${noteId}/lock`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isLocked: !isCurrentlyLocked })
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        const data = await response.json();
        setMessage(data.message);
        
        // Update the current note if it's being edited
        if (currentNote && currentNote.id === noteId) {
          setCurrentNote({ ...currentNote, isLocked: !isCurrentlyLocked });
        }
        
        // Refresh notes list to show updated status
        fetchNotes();
      } else {
        const errorData = await response.json();
        setMessage(errorData.message || 'Failed to update lock status');
      }
    } catch (error) {
      setMessage('Error updating lock status');
      console.error('Error toggling lock:', error);
    }
  };

  // Fetch all vaults - with protection against repeated calls
  const fetchVaults = async () => {
    if (vaults.length > 0) {
      console.log('Vaults already loaded, skipping fetch');
      return; // Already have vaults, don't fetch again
    }
    
    console.log('Fetching vaults from API...');
    try {
      const response = await fetch(`${API_URL}/vaults`, {
        headers: getAuthHeaders()
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        const data = await response.json();
        console.log('Vaults fetched:', data.length);
        setVaults(data);
        // Set first vault as current if none selected
        if (data.length > 0 && !currentVault) {
          console.log('Setting first vault as current:', data[0].id);
          setCurrentVault(data[0]);
        }
      } else {
        setMessage('Failed to load vaults');
      }
    } catch (error) {
      setMessage('Error loading vaults');
      console.error('Error fetching vaults:', error);
    }
  };

  // Fetch notes for current vault - simplified with useCallback
  const fetchNotes = useCallback(async () => {
    if (!currentVault) return;
    
    try {
      const response = await fetch(`${API_URL}/notes?vaultId=${currentVault.id}`, {
        headers: getAuthHeaders()
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      } else {
        setMessage('Failed to load notes');
      }
    } catch (error) {
      setMessage('Error loading notes');
      console.error('Error fetching notes:', error);
    }
  }, [currentVault]); // Dependencies for fetchNotes useCallback

  // Simple auto-save function - minimal logging
  const autoSaveNote = useCallback(async (noteTitle, noteContent, noteTags, forceCreate = false) => {
    if (!currentVault || (!forceCreate && !noteTitle.trim() && !noteContent.trim())) {
      return; // Skip silently
    }

    // Check if we have a valid token
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No authentication token found');
      setMessage('Please log in to save notes');
      return;
    }
    
    try {
      const noteData = {
        title: noteTitle.trim() || 'Untitled Note',
        content: noteContent.trim() || ' ',
        tags: noteTags,
        vaultId: currentVault.id
      };

      const url = currentNote 
        ? `${API_URL}/notes/${currentNote.id}`
        : `${API_URL}/notes`;
      
      const method = currentNote ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(noteData)
      });

      if (response.ok) {
        const savedNote = await response.json();
        if (!currentNote) {
          setCurrentNote(savedNote);
          setMessage('Note created!');
        }
        // Refresh notes list to show the updated content in sidebar
        fetchNotes();
      } else {
        const errorText = await response.text();
        console.error('Auto-save failed:', { status: response.status, statusText: response.statusText, error: errorText });
        setMessage(`Save failed: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Auto-save network error:', error);
      setMessage(`Save error: ${error.message}`);
    }
  }, [currentVault, currentNote, fetchNotes]);

  // Enhanced auto-save with change detection - simplified
  useEffect(() => {
    // Only proceed if we're editing and have a vault
    if (!isEditing || !currentVault) {
      return;
    }

    // Check if content has actually changed from the original
    const hasChanges = title !== originalTitle || 
                      content !== originalContent || 
                      JSON.stringify(tags) !== JSON.stringify(originalTags);

    // Only save if there are changes and some content
    if (!hasChanges || (!title.trim() && !content.trim() && tags.length === 0)) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }

    // Set new timeout
    const timeoutId = setTimeout(async () => {
      setIsAutoSaving(true);
      try {
        await autoSaveNote(title, content, tags);
        // Update original values after successful save
        setOriginalTitle(title);
        setOriginalContent(content);
        setOriginalTags([...tags]);
      } finally {
        setIsAutoSaving(false);
      }
    }, 1500);

    setAutoSaveTimeout(timeoutId);

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [title, content, tags, isEditing, currentVault]); // Minimal dependencies

  // Fetch note content from blob storage
  const fetchNoteContent = async (noteId) => {
    try {
      const response = await fetch(`${API_URL}/notes/${noteId}`, {
        headers: getAuthHeaders()
      });

      if (handleAuthError(response)) return null;

      if (response.ok) {
        const data = await response.json();
        return data.content;
      } else {
        console.error('Failed to fetch note content');
        return null;
      }
    } catch (error) {
      console.error('Error fetching note content:', error);
      return null;
    }
  };

  // Fetch available tags for current vault - simplified
  const fetchTags = async () => {
    if (!currentVault) return;
    
    try {
      const response = await fetch(`${API_URL}/tags?vaultId=${currentVault.id}`, {
        headers: getAuthHeaders()
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        const data = await response.json();
        setAvailableTags(data);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Load data on component mount - with logging
  useEffect(() => {
    console.log('Component mounted, fetching vaults...');
    fetchVaults();
  }, []); // Empty dependency array

  // Load notes and tags when vault changes - with logging and protection
  useEffect(() => {
    console.log('Vault changed:', currentVault?.id);
    if (currentVault) {
      console.log('Fetching notes and tags for vault:', currentVault.id);
      fetchNotes();
      fetchTags();
    }
  }, [currentVault?.id]); // Only depend on vault ID, not whole object

  // Auto-dismiss message after 2 seconds
  useEffect(() => {
    if (message && !isMessageFadingOut) {
      const timer = setTimeout(() => {
        setIsMessageFadingOut(true);
        // After fade-out animation completes (300ms), clear the message
        setTimeout(() => {
          setMessage('');
          setIsMessageFadingOut(false);
        }, 300);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [message, isMessageFadingOut]);

  // Update time remaining every minute
  useEffect(() => {
    const interval = setInterval(() => {
      // Force a re-render to update time remaining displays
      setNotes(prevNotes => [...prevNotes]);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  // Handle click outside vault dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showVaultDropdown && !event.target.closest('.custom-select-wrapper')) {
        setShowVaultDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVaultDropdown]);

  // Create or update note
  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      setMessage('Title and content are required');
      return;
    }

    if (!currentVault) {
      setMessage('Please select a vault first');
      return;
    }

    const noteData = {
      title: title.trim(),
      content: content.trim(),
      tags: tags,
      vaultId: currentVault.id
      // Don't auto-lock new notes - let them start as temporary with watermark
    };

    try {
      let response;
      if (currentNote) {
        // Update existing note
        response = await fetch(`${API_URL}/notes/${currentNote.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(noteData)
        });
      } else {
        // Create new note
        response = await fetch(`${API_URL}/notes`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(noteData)
        });
      }

      if (handleAuthError(response)) return;

      if (response.ok) {
        const savedNote = await response.json();
        setMessage(currentNote ? 'Note updated!' : 'Note created! Click lock to save permanently.');
        
        if (currentNote) {
          // If updating existing note, just refresh
          setTitle('');
          setContent('');
          setTags([]);
          setCurrentNote(null);
          setIsEditing(false);
        } else {
          // If creating new note, keep it selected to show watermark
          setCurrentNote(savedNote);
          // Keep editing mode active so user can see the watermark and lock button
        }
        
        fetchNotes();
        fetchTags();
      } else {
        const data = await response.json();
        setMessage(data.message || 'Failed to save note');
      }
    } catch (error) {
      setMessage('Error saving note');
      console.error('Error saving note:', error);
    }
  };

  // Create new vault
  const handleCreateVault = async () => {
    if (!newVaultName.trim()) {
      setMessage('Vault name is required');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/vaults`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: newVaultName.trim(),
          description: newVaultDescription.trim()
        })
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        const newVault = await response.json();
        setMessage('Vault created successfully!');
        setNewVaultName('');
        setNewVaultDescription('');
        setShowVaultModal(false);
        fetchVaults();
        setCurrentVault(newVault);
      } else {
        const data = await response.json();
        setMessage(data.message || 'Failed to create vault');
      }
    } catch (error) {
      setMessage('Error creating vault');
      console.error('Error creating vault:', error);
    }
  };

  // Switch vault
  const handleVaultChange = (vault) => {
    setCurrentVault(vault);
    setCurrentNote(null);
    setTitle('');
    setContent('');
    setTags([]);
    setIsEditing(false);
    setSearchQuery('');
    setSelectedTag('');
    
    // Reset original values when switching vaults
    setOriginalTitle('');
    setOriginalContent('');
    setOriginalTags([]);
  };

  // Edit note
  const handleEdit = async (note) => {
    setCurrentNote(note);
    setTitle(note.title);
    setTags(note.tags || []);
    setIsEditing(true);
    
    // Fetch content from blob storage if not available
    if (!note.content) {
      const content = await fetchNoteContent(note.id);
      const loadedContent = content || '';
      setContent(loadedContent);
      
      // Set original values for change tracking
      setOriginalTitle(note.title);
      setOriginalContent(loadedContent);
      setOriginalTags([...(note.tags || [])]);
    } else {
      setContent(note.content);
      
      // Set original values for change tracking
      setOriginalTitle(note.title);
      setOriginalContent(note.content);
      setOriginalTags([...(note.tags || [])]);
    }
  };

  // Delete note
  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/notes/${noteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (handleAuthError(response)) return;

      if (response.ok) {
        setMessage('Note deleted!');
        fetchNotes();
        if (currentNote && currentNote.id === noteId) {
          setCurrentNote(null);
          setTitle('');
          setContent('');
          setTags([]);
          setIsEditing(false);
        }
      } else {
        setMessage('Failed to delete note');
      }
    } catch (error) {
      setMessage('Error deleting note');
      console.error('Error deleting note:', error);
    }
  };

  // Toggle favorite
  const toggleFavorite = async (note) => {
    try {
      const response = await fetch(`${API_URL}/notes/${note.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isFavorite: !note.isFavorite })
      });

      if (response.ok) {
        fetchNotes();
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Cancel edit
  const handleCancel = () => {
    setCurrentNote(null);
    setTitle('');
    setContent('');
    setTags([]);
    setIsEditing(false);
    
    // Reset original values when canceling
    setOriginalTitle('');
    setOriginalContent('');
    setOriginalTags([]);
  };

  // Search notes
  const handleSearch = async () => {
    if (!currentVault) return;
    
    if (!searchQuery.trim()) {
      fetchNotes();
      return;
    }

    try {
      const response = await fetch(`${API_URL}/notes/search/${encodeURIComponent(searchQuery)}?vaultId=${currentVault.id}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (error) {
      console.error('Error searching notes:', error);
    }
  };

  // Filter by tag
  const handleFilterByTag = async (tag) => {
    if (!currentVault) return;
    
    if (!tag) {
      fetchNotes();
      setSelectedTag('');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/notes/tag/${encodeURIComponent(tag)}?vaultId=${currentVault.id}`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setNotes(data);
        setSelectedTag(tag);
      }
    } catch (error) {
      console.error('Error filtering by tag:', error);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className="notes-container">
        <div className={`notes-sidebar ${showSidebar ? 'show' : 'hide'}`}>
          {/* Vault Selection */}
          <div className="vault-section">
            <div className="vault-header">
              <h3>Secure Vaults</h3>
              <button 
                className="btn-new-vault"
                onClick={() => setShowVaultModal(true)}
                title="Create New Vault"
              >
                + Vault
              </button>
            </div>
            <div className="vault-selector">
              <select 
                value={currentVault?.id || ''} 
                onChange={(e) => {
                  const vault = vaults.find(v => v.id === e.target.value);
                  if (vault) handleVaultChange(vault);
                }}
                className="vault-select"
              >
                <option value="">Select a vault...</option>
                {vaults.map(vault => (
                  <option key={vault.id} value={vault.id}>
                    {vault.name} {vault.isDefault ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {currentVault && (
              <div className="current-vault-info">
                <small>
                  <strong>{currentVault.name}</strong>
                  {currentVault.description && <><br />{currentVault.description}</>}
                </small>
              </div>
            )}
          </div>

          <div className="notes-header">
            <h2>My Notes</h2>
            <button 
              className="btn-new-note"
              onClick={async () => {
                if (!currentVault) {
                  setMessage('Please select a vault first');
                  return;
                }
                setCurrentNote(null);
                setTitle('');
                setContent('');
                setTags([]);
                setIsEditing(true);
                
                // Set original values for new note (empty)
                setOriginalTitle('');
                setOriginalContent('');
                setOriginalTags([]);
                
                // Immediately create a new note with minimal content
                setTimeout(() => {
                  autoSaveNote('', '', [], true); // Force create even with empty content
                }, 100);
              }}
              disabled={!currentVault}
              title={!currentVault ? 'Select a vault first' : 'Create New Note'}
            >
              + New Note
            </button>
          </div>

          {/* Search */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Search notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="search-input"
            />
            <button onClick={handleSearch} className="search-btn">
              <SearchIcon size={18} />
            </button>
          </div>

          {/* Tag filter */}
          <div className="tag-filter">
            <select 
              value={selectedTag} 
              onChange={(e) => handleFilterByTag(e.target.value)}
              className="tag-select"
            >
              <option value="">All tags</option>
              {availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          {/* Notes list */}
          <div className="notes-list">
            {notes
              .sort((a, b) => {
                // Bookmarked notes first (isFavorite true comes before false)
                if (a.isFavorite !== b.isFavorite) {
                  return b.isFavorite - a.isFavorite;
                }
                // Then sort by updated date (most recent first)
                return new Date(b.updatedAt) - new Date(a.updatedAt);
              })
              .map(note => (
              <div 
                key={note.id} 
                className={`note-item ${currentNote?.id === note.id ? 'selected' : ''}`}
                onClick={() => handleEdit(note)}
              >
                <div className="note-item-header">
                  <h4>{note.title}</h4>
                  <div className="note-actions">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(note);
                      }}
                      className={`btn-favorite ${note.isFavorite ? 'favorited' : ''}`}
                    >
                      <BookmarkIcon filled={note.isFavorite} size={18} />
                    </button>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(note.id);
                      }}
                      className="btn-delete"
                    >
                      <DeleteIcon size={18} />
                    </button>
                  </div>
                </div>
                <p className="note-preview">
                  {createPreview(note.content, 100)}
                </p>
                <div className="note-meta">
                  <small>{formatDate(note.updatedAt)}</small>
                  {note.tags.length > 0 && (
                    <div className="note-tags">
                      {note.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Time remaining watermark */}
                {!note.isLocked && note.expiresAt && (
                  <div className="time-watermark">
                    {getTimeRemaining(note.expiresAt)}
                  </div>
                )}
                {/* Lock status indicator */}
                {note.isLocked && (
                  <div className="lock-indicator">
                    <LockIcon size={14} locked={true} /> Saved
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="notes-editor">
          <div className="editor-header">
            <button 
              className="sidebar-toggle"
              onClick={() => setShowSidebar(!showSidebar)}
            >
              {showSidebar ? '◀' : '▶'}
            </button>
            <h3>{isEditing ? (currentNote ? 'Edit Note' : 'New Note') : 'Select a note to edit'}</h3>
            {isAutoSaving && (
              <span className="auto-save-indicator">💾 Saving...</span>
            )}
          </div>

          {isEditing && (
            <div className="editor-form">
              <input
                type="text"
                placeholder="Note title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="title-input"
              />
              
              {/* Turquoise Rich Text Editor */}
              <TurquoiseEditor
                value={content}
                onChange={(newContent) => {
                  setContent(newContent);
                }}
                placeholder="Write your note here..."
              />
              
              <div className="tags-section">
                <div className="tags-display">
                  {tags.map((tag, index) => (
                    <span key={index} className="tag-button">
                      {tag}
                      <button 
                        className="tag-remove"
                        onClick={() => removeTag(tag)}
                        type="button"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Type a tag and press Enter..."
                  value={currentTagInput}
                  onChange={(e) => setCurrentTagInput(e.target.value)}
                  onKeyPress={handleTagInputKeyPress}
                  className="tag-input"
                />
              </div>
              
              {/* Editor watermark for time remaining */}
              {currentNote && !currentNote.isLocked && currentNote.expiresAt && (
                <div className="editor-watermark">
                  ⚠️ {getTimeRemaining(currentNote.expiresAt)} - Click lock to save permanently
                </div>
              )}
              
              <div className="editor-actions">
                <button 
                  onClick={() => {
                    if (currentNote) {
                      // For existing notes, toggle lock status
                      const isCurrentlyLocked = currentNote.isLocked === true;
                      toggleNoteLock(currentNote.id, isCurrentlyLocked);
                    } else {
                      // If somehow no note exists yet, force create one
                      autoSaveNote(title, content, tags, true);
                    }
                  }}
                  className={`btn-lock ${currentNote?.isLocked === true ? 'locked' : ''}`}
                >
                  {currentNote?.isLocked === true ? 
                    <><LockIcon size={16} locked={false} /> Unlock</> : 
                    <><LockIcon size={16} locked={true} /> Lock</>
                  }
                </button>
                <button onClick={handleCancel} className="btn-cancel">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!isEditing && !currentNote && (
            <div 
              className={`welcome-message ${currentVault ? 'clickable' : ''}`}
              onClick={currentVault ? async () => {
                setCurrentNote(null);
                setTitle('');
                setContent('');
                setTags([]);
                setIsEditing(true);
                
                // Immediately create a new note with minimal content
                setTimeout(() => {
                  autoSaveNote('', '', [], true); // Force create even with empty content
                }, 100);
              } : undefined}
            >
              {!currentVault ? (
                <>
                  <h3>Welcome to your secure note vaults! <LockIcon size={20} locked={true} /></h3>
                  <p>Select or create a vault to start organizing your notes securely.</p>
                </>
              ) : (
                <>
                  <h3>Vault: {currentVault.name} <VaultIcon size={40} /></h3>
                  <p>Click anywhere to create a note instantly - timer starts immediately!</p>
                  <p><small>Your note auto-saves as you type. Click lock to save permanently.</small></p>
                </>
              )}
            </div>
          )}

          {message && <div className={`message ${isMessageFadingOut ? 'fade-out' : ''}`}>{message}</div>}
        </div>

        {/* Vault Creation Modal */}
        {showVaultModal && (
          <div className="modal-overlay" onClick={() => setShowVaultModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Create New Secure Vault</h3>
              <p>Create a new encrypted vault to organize your notes securely.</p>
              
              <input
                type="text"
                placeholder="Vault name..."
                value={newVaultName}
                onChange={(e) => setNewVaultName(e.target.value)}
                className="vault-input"
                maxLength={50}
              />
              
              <textarea
                placeholder="Vault description (optional)..."
                value={newVaultDescription}
                onChange={(e) => setNewVaultDescription(e.target.value)}
                className="vault-textarea"
                rows={3}
                maxLength={200}
              />
              
              <div className="modal-actions">
                <button onClick={handleCreateVault} className="btn-create-vault">
                  <LockIcon size={16} locked={true} /> Create Vault
                </button>
                <button onClick={() => {
                  setShowVaultModal(false);
                  setNewVaultName('');
                  setNewVaultDescription('');
                }} className="btn-cancel-vault">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </>
  );
}